import { useCallback, useEffect, useRef, useState } from 'react';
import { socialPostsService } from '@services';
import { CREATOR_INFO_STATUS } from './tiktokRules';

const EMPTY = { creators: [], isAudited: true };

// Adding locations one at a time must not fan out one TikTok call per keystroke.
const DEBOUNCE_MS = 400;

// One TikTok round-trip per distinct location set; re-renders and tab switches reuse the result.
export const useTikTokCreatorInfo = ({ enabled, locationIds = [] }) => {
    const key = enabled && locationIds.length ? [...locationIds].sort((a, b) => a - b).join(',') : '';
    const [debouncedKey, setDebouncedKey] = useState(key);
    const [state, setState] = useState({ key: '', data: EMPTY, status: CREATOR_INFO_STATUS.IDLE });
    const [attempt, setAttempt] = useState(0);
    // TikTok wants the latest creator info when the page renders, so the first read and every
    // explicit retry reach TikTok. Changing locations reuses the snapshot, so editing cannot
    // rate-limit the account against itself.
    const forceNextRef = useRef(true);

    useEffect(() => {
        if (debouncedKey === key) return undefined;
        const timer = setTimeout(() => setDebouncedKey(key), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [key, debouncedKey]);

    useEffect(() => {
        if (!debouncedKey) return undefined;
        let cancelled = false;
        setState({ key: debouncedKey, data: EMPTY, status: CREATOR_INFO_STATUS.LOADING });
        const refresh = forceNextRef.current;
        forceNextRef.current = false;
        socialPostsService
            .getTikTokCreatorInfo(debouncedKey.split(',').map(Number), refresh)
            .then(data => {
                if (cancelled) return;
                setState({ key: debouncedKey, data: data || EMPTY, status: CREATOR_INFO_STATUS.READY });
            })
            .catch(() => {
                if (cancelled) return;
                setState({ key: debouncedKey, data: EMPTY, status: CREATOR_INFO_STATUS.ERROR });
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedKey, attempt]);

    const refetch = useCallback(() => {
        forceNextRef.current = true;
        setAttempt(a => a + 1);
    }, []);

    if (!key) return { ...EMPTY, status: CREATOR_INFO_STATUS.IDLE, refetch };
    const current = state.key === key ? state : { data: EMPTY, status: CREATOR_INFO_STATUS.LOADING };
    return {
        creators: current.data.creators || [],
        isAudited: current.data.isAudited !== false,
        status: current.status,
        refetch,
    };
};
